import { useCallback, useMemo } from 'react';

import Cookies from 'universal-cookie';

import type { FormData } from '../types';

interface AdvancedConfigurationFormData {
  cookies?: Record<string, string>;
  fieldMappings?: Record<string, string>;
  queryParams?: Record<string, string>;
}

export interface AdvancedConfigurationPostSubmitData {
  dataLayer?: {
    [key: string]: unknown;
    event: string;
  };
  disableFormSubmitSuccessDataLayer?: boolean;
}

export interface AdvancedConfigurationSystemData {
  [key: string]: unknown;
  bot_id?: string;
  OpenpriseEnabled?: boolean;
  Version?: 'new' | 'old';
}

const getSearchParams = () => {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
};

/**
 * {
 *   formData: {
 *     cookies: {
 *       cookieName: 'payloadKey',
 *     },
 *     queryParams: {
 *       queryParamName: 'payloadKey',
 *     },
 *     fieldMappings: {
 *       formFieldName: 'payloadKey',
 *     },
 *   },
 *   systemData: {
 *     bot_id: string,
 *     OpenpriseEnabled: boolean,
 *     Version: 'new' | 'old',
 *   },
 *   postSubmitData: {
 *     disableFormSubmitSuccessDataLayer: boolean,
 *     dataLayer: {
 *       event: string, // e.g., "Form Submit - Unsubscribe"
 *       [key: string]: unknown, // Allows additional unknown key-value pairs
 *     }
 *   }
 * }
 */

export interface AdvancedConfiguration {
  formData?: AdvancedConfigurationFormData;
  postSubmitData?: AdvancedConfigurationPostSubmitData;
  systemData?: AdvancedConfigurationSystemData;
}

const getCookieFormData = (config: AdvancedConfiguration) => {
  const mappings = config?.formData?.cookies;

  if (!mappings) {
    return null;
  }

  const cookieInstance = new Cookies();
  const cookieFormData: Record<string, string> = Object.keys(mappings).reduce((acc, cookieName) => {
    const payloadKey = mappings[cookieName];

    return {
      ...acc,
      [payloadKey]: cookieInstance.get(cookieName),
    };
  }, {});

  return cookieFormData;
};

const getQueryParamsFormData = (config: AdvancedConfiguration) => {
  const mappings = config?.formData?.queryParams;

  if (!mappings) {
    return null;
  }

  const searchParams = getSearchParams();
  const configParams: Record<string, string> = Object.keys(mappings).reduce((acc, queryParam) => {
    const payloadKey = mappings[queryParam];

    return {
      ...acc,
      [payloadKey]: searchParams.get(queryParam),
    };
  }, {});

  return configParams;
};

const useFormAdvancedConfiguration = (config: AdvancedConfiguration) => {
  const additionalFormData = useMemo(() => {
    return {
      ...getCookieFormData(config),
      ...getQueryParamsFormData(config),
    };
  }, [config]);

  // Remap the form data according to the fieldMappings configuration
  const remapToOpenpriseKeysFn = useCallback((formData: FormData) => {
    const fieldMappings = config?.formData?.fieldMappings;

    if (!fieldMappings) {
      return formData;
    }

    const finalPayload: Record<string, unknown> = Object.keys(formData).reduce((acc, formKey) => {
      const fieldMapping = fieldMappings[formKey] || formKey;

      return {
        ...acc,
        [fieldMapping]: formData[formKey],
      };
    }, {});

    return finalPayload;
  }, [additionalFormData, config?.formData?.fieldMappings]);

  return {
    additionalFormData,
    advancedConfigurationPostSubmitData: config?.postSubmitData,
    advancedConfigurationSystemData: config?.systemData ?? {},
    remapToOpenpriseKeysFn,
  } as const;
};

export default useFormAdvancedConfiguration;
